import { sessionKeys } from "@features/history/hooks/useSessions";
import { useChatSurface } from "@layouts/AppLayout/useLayoutMode";
import { useChatStore } from "@stores/chatStore";
import { useOrgStore } from "@stores/orgStore";
import { useProjectStore } from "@stores/projectStore";
import { useQueryClient } from "@tanstack/react-query";
import { normalizeMessages } from "@utils/mermaidExtract";
import { useCallback, useEffect, useRef } from "react";
import { sessionsApi } from "../services/api";
import { cancelReattach, reattachToSession } from "./chatReattach";
import { chatAbort } from "./chatTurnRefs";
import { useNavigateChat } from "./useNavigateChat";

/**
 * Session lifecycle — owns the gestures that switch *which*
 * conversation the user is in: load by id, start fresh, clear, page
 * older history, react to active-project changes.
 *
 * Cleanly separated from the streaming/turn loop in `useChatStream`:
 * lifecycle is sync UI-state work plus opportunistic refetches; the
 * stream is the long-lived async SSE pipeline. Splitting lets the
 * streaming logic be tested without dragging in the project-change
 * effect, and lets `loadSessionById` be reasoned about without the
 * 250-line turn body in scope.
 */
export interface SessionApi {
  /**
   * Load a session by id and route the chat surface to the right shell.
   *
   * `projectId` is the session's `project_id` (string for project-bound,
   * null for standalone). When provided, it overrides the URL-derived
   * shell so a project-bound session clicked from `/chat` lands the
   * user on `/projects/$id/chat?s=<id>` instead of leaving them on
   * `/chat` with a session that doesn't match the visible context.
   * Omit the arg to preserve the current shell (the previous behaviour).
   */
  loadSessionById: (
    sid: string,
    title?: string,
    projectId?: string | null,
  ) => void;
  startNewSession: () => void;
  clearConversation: () => void;
  loadOlderMessages: () => Promise<void>;
}

export function useSession(): SessionApi {
  const qc = useQueryClient();
  const navigateChat = useNavigateChat();
  const activeProject = useProjectStore((s) => s.activeProject);
  // Org context — `sessionsApi.list` filters personal vs. org sessions
  // based on this. Without it, the auto-load fetches personal-only and
  // returns empty for users working inside an organisation, which trips
  // the "no rows + clear" branch and snaps the rail back to its empty
  // state right after a session is selected/created.
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  // Surface-aware session loading. Each (project, surface) pair has its
  // own conversation thread — the rail on Workbench shows Workbench
  // sessions, the rail on Requirements shows Requirements sessions.
  // Null surface (standalone /chat) skips the auto-load entirely so
  // the dedicated chat page keeps its existing behaviour.
  const surface = useChatSurface();
  // Track the previous active project so we can detect changes.
  const prevProjectRef = useRef<string | null>(null);
  // Track the previous surface so a Workbench↔Requirements toggle
  // re-runs the auto-load and swaps the visible conversation.
  const prevSurfaceRef = useRef<string | null>(null);

  // When active project changes, start a new session automatically.
  // The project change is the only signal the effect actually wants to
  // react to — messages.length / sessionId are read via getState() so
  // mid-flight mutations don't re-trigger the project-change branch.
  useEffect(() => {
    if (prevProjectRef.current === null) {
      // First render — just record the initial value.
      prevProjectRef.current = activeProject;
      return;
    }
    if (prevProjectRef.current !== activeProject) {
      prevProjectRef.current = activeProject;
      const st = useChatStore.getState();
      if (st.messages.length > 0 || st.sessionId !== null) {
        st.clearMessages();
        // Drop `?s=<id>` too — otherwise ChatPage's hydration effect
        // would re-fetch the old session and put it right back.
        navigateChat({});
      }
    }
  }, [activeProject, navigateChat]);

  // Auto-load the latest session for (activeProject, surface) whenever
  // either changes. Without this, the global chatStore singleton bleeds
  // a Requirements session into the Workbench rail (or vice versa) the
  // moment the user navigates between them.
  //
  // Sequencing:
  //   1. Surface or project changed → fetch the latest session row for
  //      (project, surface) from /v1/sessions?project_id=...&surface=...
  //   2. If the cached store sessionId already matches the fetched
  //      session, do nothing — the user is already on the right one.
  //   3. If a session exists, hand it to `loadSessionByIdRef.current`
  //      (clears the chat, fetches messages, rehydrates).
  //   4. If none exists, clear the store so the rail shows the empty
  //      state (where the user can type the first message — at which
  //      point sendMessage creates a new (project, surface)-pinned
  //      session).
  //
  // `loadSessionById` is hoisted into a ref so the effect doesn't
  // depend on its identity (which churns on `qc` invalidation). We
  // assign the ref below, after the callback is defined; the effect
  // reads `.current` at fire time.
  const loadSessionByIdRef = useRef<
    (sid: string, title?: string, projectId?: string | null) => void
  >(() => {});
  useEffect(() => {
    if (!surface || !activeProject) {
      prevSurfaceRef.current = surface;
      return;
    }
    const surfaceChanged = prevSurfaceRef.current !== surface;
    prevSurfaceRef.current = surface;

    let cancelled = false;
    void (async () => {
      try {
        // Latest session for this (project, surface) pair. The backend
        // sorts by last_active DESC so row[0] is the most recent.
        // Pass activeOrgId so org members see org sessions; passing
        // undefined here would scope to personal-only and return empty
        // for org-scoped projects, tripping the clear branch below.
        const rows = await sessionsApi.list(
          activeOrgId,
          activeProject,
          surface,
        );
        if (cancelled) return;
        const latest = rows[0];
        const st = useChatStore.getState();
        if (latest) {
          // Already on the right session? No-op.
          if (st.sessionId === latest.session_id) return;
          // Different session — load it. Critically, do NOT pass the
          // project id: that signals "promote to project shell" to
          // `useNavigateChat` (overridden=true), which falls through
          // to the canonical `/projects/$pid/chat` and yanks the user
          // off Requirements. Since we filtered the list by the active
          // project on the way in, the loaded session's project is
          // already the URL's project — no shell change is needed.
          loadSessionByIdRef.current(latest.session_id, latest.title || "");
        } else if (surfaceChanged && (st.messages.length > 0 || st.sessionId)) {
          // No session for this surface yet — clear so the rail shows
          // the empty/welcome state. Only do this on a real surface
          // change to avoid clearing a fresh session the user just
          // started but that hasn't propagated through the cache yet.
          st.clearMessages();
          navigateChat({});
        }
      } catch {
        // Network blip — leave the current store state alone. Worst
        // case the user sees the previous surface's chat for a beat
        // and a manual "+ New chat" fixes it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [surface, activeProject, activeOrgId, navigateChat]);

  const startNewSession = useCallback(() => {
    chatAbort.current?.abort();
    // Tear down any reattach in flight — we're moving away from the
    // active session, so its live updates are no longer wanted.
    cancelReattach();
    const st = useChatStore.getState();
    st.setStreaming(false);
    st.setReconnecting(false);
    st.resetStreamingContent();
    st.clearMessages();
    // Drop the ?s=<id> search param so a refresh lands on the blank
    // welcome screen instead of trying to rehydrate the session we just
    // cleared.
    navigateChat({});
  }, [navigateChat]);

  const loadSessionById = useCallback(
    (sid: string, title?: string, projectId?: string | null) => {
      // Phase 1 — synchronous switch. Runs before any network work so
      // the click feels instant: chat window flips to the new session,
      // sidebar's active-row highlight updates, URL bar reflects the
      // new ?s=<id>. Hydration happens in phase 2.
      chatAbort.current?.abort();
      // Tear down any in-flight reattach from a prior session — the
      // helper also self-cancels on re-entry, but doing it here too
      // prevents a stale "Reconnecting…" banner from flashing while
      // the new session is mid-fetch.
      cancelReattach();
      const st = useChatStore.getState();
      st.setStreaming(false);
      st.setReconnecting(false);
      st.resetStreamingContent();
      // Clear the visible message list immediately. The skeleton in
      // ChatContainer renders against `isHydrating && messages.length === 0`,
      // so an empty list during fetch is what makes the loading state
      // surface in the chat window (rather than over the sidebar row title).
      st.setMessages([], { hasMoreOlder: false, oldestTimestamp: null });
      st.setSessionId(sid);
      st.setSessionTitle(title ?? "");
      st.setHydrating(true);
      // When the caller hands us an explicit projectId we're about to
      // navigate the user into a different project shell. ProjectLayout-
      // Page will then mirror the URL projectId into the store, which
      // would normally wake the activeProject effect above and clear
      // the session we just loaded. Pre-syncing `prevProjectRef` to the
      // imminent target keeps the effect's "did the project change?"
      // check false on settle, so the auto-promotion stays a clean
      // navigation rather than a clear-and-reload.
      if (projectId !== undefined) {
        prevProjectRef.current = projectId;
      }
      // Put the session id in the URL so refresh / deep-linking keeps
      // us on the same conversation. `replace` avoids polluting history
      // with intermediate states when the sidebar opens a session.
      navigateChat(
        { s: sid },
        projectId !== undefined ? { projectId } : undefined,
      );

      // Phase 2 — fetch the page, then ask the recovery helper if the
      // run is still live. Captures `sid` and re-checks the store's
      // current sessionId before applying any result, so a click-A-
      // then-click-B sequence can't have A's late response pollute B.
      void (async () => {
        try {
          // Cache the page under the same key the sidebar previously
          // used (sessionKeys.messages) so a quick back-and-forth
          // between two chats doesn't re-fetch within TanStack's
          // staleTime.
          const page = await qc.fetchQuery({
            queryKey: sessionKeys.messages(sid),
            queryFn: () => sessionsApi.getMessages(sid),
          });
          if (useChatStore.getState().sessionId !== sid) return;
          useChatStore
            .getState()
            .setMessages(normalizeMessages(page.messages), {
              hasMoreOlder: page.has_more,
              oldestTimestamp: page.oldest_timestamp,
            });
        } catch {
          // Leave the empty state in place. Sidebar can be retried by
          // re-clicking. A toast would be noisy for a transient blip.
        } finally {
          if (useChatStore.getState().sessionId === sid) {
            useChatStore.getState().setHydrating(false);
          }
        }

        // Bail if a newer session has been selected while we were
        // fetching — without this guard a stale page-fetch would still
        // call reattachToSession(old_sid), cancelling the new owner's
        // reattach and starting one for a session the user has left.
        if (useChatStore.getState().sessionId !== sid) return;

        await reattachToSession(sid, qc);
      })();
    },
    [navigateChat, qc],
  );

  // Pin the latest `loadSessionById` into the ref the surface effect
  // reads from. Doing this in render (not in an effect) is fine: the
  // ref is read inside an async callback that fires later, so the
  // assignment ordering is right by the time it matters.
  loadSessionByIdRef.current = loadSessionById;

  const loadOlderMessages = useCallback(async () => {
    // Read latest store state synchronously instead of relying on the
    // closure capture so a quick double-click doesn't fire two requests
    // for the same cursor.
    const st = useChatStore.getState();
    if (!st.sessionId || !st.hasMoreOlder || st.isLoadingOlder) return;
    if (st.oldestTimestamp == null) return;
    const sid = st.sessionId;
    const cursor = st.oldestTimestamp;
    st.setLoadingOlder(true);
    try {
      const page = await sessionsApi.getMessages(sid, { beforeTs: cursor });
      // Re-check the session id — the user may have switched chats
      // while the request was in flight; we don't want to splice old
      // slices into a different session.
      if (useChatStore.getState().sessionId !== sid) return;
      useChatStore
        .getState()
        .prependMessages(normalizeMessages(page.messages), {
          hasMoreOlder: page.has_more,
          oldestTimestamp:
            // Empty page (shouldn't happen if has_more was true, but
            // guard anyway): keep the previous cursor so a retry uses
            // the same boundary instead of resetting to null.
            page.oldest_timestamp ?? cursor,
        });
    } catch {
      // Leave hasMoreOlder true so the user can retry; a toast would be
      // noisy for a transient network blip.
    } finally {
      useChatStore.getState().setLoadingOlder(false);
    }
  }, []);

  const clearConversation = useCallback(() => {
    useChatStore.getState().clearMessages();
  }, []);

  return {
    loadSessionById,
    startNewSession,
    clearConversation,
    loadOlderMessages,
  };
}
